// Supabase Status Popover - Shows connection status in toolbar (same pattern as DeployStatusPopover)
import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Database, Check, Plus, Lock, ExternalLink } from 'lucide-react';
import { useSupabaseMCP } from '@/hooks/useSupabaseMCP';
import { CreateSupabaseProjectDialog } from '@/components/integrations/CreateSupabaseProjectDialog';
import { PxMark } from '@/components/PxMark';
import { cn } from '@/lib/utils';

interface SupabaseStatusPopoverProps {
    pixelProjectId?: string;
    onOpenPanel?: () => void;
}

type ConnectionStatus = 'disconnected' | 'connecting' | 'no-project' | 'connected';

// Boutons OFFSET partagés dans ce popover
const BTN_SECONDARY = "flex w-full items-center justify-center gap-2 border border-border bg-card px-3 py-2 text-xs font-medium text-foreground transition-colors hover:bg-secondary disabled:opacity-40";
const BTN_PRIMARY = "flex w-full items-center justify-center gap-2 bg-primary px-3 py-2.5 text-xs font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-40";

export function SupabaseStatusPopover({ pixelProjectId, onOpenPanel }: SupabaseStatusPopoverProps) {
    const [isOpen, setIsOpen] = useState(false);
    const [isLoadingProjects, setIsLoadingProjects] = useState(false);
    const [popoverStyle, setPopoverStyle] = useState<React.CSSProperties>({});
    const triggerRef = useRef<HTMLButtonElement>(null);

    const {
        isConnected,
        isLoading,
        selectedProjectName,
        selectedProject,
        projects,
        fetchProjects,
        selectProject,
        disconnect,
        connectWithOAuth,
    } = useSupabaseMCP(pixelProjectId);

    // Calculate popover position when opening
    useEffect(() => {
        if (isOpen && triggerRef.current) {
            const rect = triggerRef.current.getBoundingClientRect();
            setPopoverStyle({
                position: 'fixed',
                top: rect.bottom + 8,
                left: rect.left, // Align left with trigger button
                zIndex: 9999,
            });
        }
    }, [isOpen]);

    // Determine status
    const status: ConnectionStatus = isLoading
        ? 'connecting'
        : !isConnected
            ? 'disconnected'
            : !selectedProject
                ? 'no-project'
                : 'connected';

    const handleLoadProjects = async () => {
        setIsLoadingProjects(true);
        await fetchProjects();
        setIsLoadingProjects(false);
    };

    const handleSelectProject = async (ref: string, name: string, usedByOther: boolean) => {
        if (usedByOther) return;
        await selectProject(ref, name);
        setIsOpen(false);
    };

    // États en tokens sémantiques — le vert Supabase ne vit que dans son logo
    const statusColor = {
        disconnected: 'text-err',
        connecting: 'text-muted-foreground',
        'no-project': 'text-warn',
        connected: 'text-ok',
    };

    const statusLabel = {
        disconnected: 'Non connecté',
        connecting: 'Connexion…',
        'no-project': 'Aucun projet',
        connected: selectedProjectName || 'Connecté',
    };

    const statusDot = {
        disconnected: 'bg-err',
        connecting: 'bg-warn',
        'no-project': 'bg-warn',
        connected: 'bg-ok',
    };

    return (
        <>
            {/* Déclencheur */}
            <button
                ref={triggerRef}
                onClick={() => setIsOpen(!isOpen)}
                className={cn(
                    "flex items-center gap-1.5 p-1.5 transition-colors",
                    isOpen ? "bg-secondary" : "hover:bg-secondary",
                    statusColor[status]
                )}
                title={statusLabel[status]}
            >
                <Database className="h-3.5 w-3.5" />
                <span className={cn("h-1.5 w-1.5 rounded-full", statusDot[status])} />
            </button>

            {/* Popover rendered via Portal to escape overflow:hidden */}
            {isOpen && createPortal(
                <>
                    {/* Backdrop */}
                    <div
                        className="fixed inset-0"
                        style={{ zIndex: 9998 }}
                        onClick={() => setIsOpen(false)}
                    />

                    {/* Panneau */}
                    <div
                        className="w-72 border border-hairline-strong bg-popover py-2"
                        style={popoverStyle}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* En-tête : dot sémantique + label mono */}
                        <div className="mb-2 border-b border-border px-3 pb-2">
                            <div className="flex items-center gap-2">
                                <span className={cn("h-1.5 w-1.5 rounded-full", statusDot[status])} />
                                <span className="truncate font-mono text-[11px] uppercase tracking-wider text-foreground">
                                    {statusLabel[status]}
                                </span>
                            </div>
                        </div>

                        {/* Contenu */}
                        <div className="space-y-3 px-3">
                            {/* Non connecté */}
                            {status === 'disconnected' && (
                                <>
                                    <p className="text-xs text-muted-foreground">
                                        Connectez Supabase pour ajouter une base de données.
                                    </p>
                                    <button
                                        onClick={() => connectWithOAuth()}
                                        className={BTN_PRIMARY}
                                    >
                                        <Database className="h-3 w-3" />
                                        Connecter Supabase
                                    </button>
                                </>
                            )}

                            {/* Connexion en cours */}
                            {status === 'connecting' && (
                                <div className="flex flex-col items-center gap-3 py-4">
                                    <PxMark state="thinking" size={20} />
                                    <p className="text-xs text-muted-foreground">Connexion…</p>
                                </div>
                            )}

                            {/* Aucun projet sélectionné */}
                            {status === 'no-project' && (
                                <>
                                    <p className="text-xs text-muted-foreground">
                                        Sélectionnez ou créez un projet Supabase.
                                    </p>

                                    {/* Charger les projets — secondaire */}
                                    <button
                                        onClick={handleLoadProjects}
                                        disabled={isLoadingProjects}
                                        className={BTN_SECONDARY}
                                    >
                                        {isLoadingProjects ? (
                                            <>
                                                <PxMark state="generating" size={10} />
                                                Chargement…
                                            </>
                                        ) : (
                                            <>
                                                <Database className="h-3 w-3" />
                                                Charger les projets
                                            </>
                                        )}
                                    </button>

                                    {/* Créer un projet — primaire encre */}
                                    <CreateSupabaseProjectDialog
                                        pixelProjectId={pixelProjectId}
                                        trigger={
                                            <button className={BTN_PRIMARY}>
                                                <Plus className="h-3 w-3" />
                                                Créer un projet
                                            </button>
                                        }
                                    />

                                    {/* Project list */}
                                    {projects.length > 0 && (
                                        <div className="border-t border-border pt-2">
                                            <p className="mb-2 font-mono text-[10px] uppercase tracking-wider text-faint">
                                                Projets disponibles
                                            </p>
                                            <div className="max-h-32 space-y-1 overflow-y-auto">
                                                {projects.map((project) => {
                                                    const isUsedByOther = project.usedByPixelProjectId &&
                                                        project.usedByPixelProjectId !== pixelProjectId;

                                                    return (
                                                        <button
                                                            key={project.id}
                                                            onClick={() => handleSelectProject(project.id, project.name, !!isUsedByOther)}
                                                            disabled={!!isUsedByOther}
                                                            className={cn(
                                                                "flex w-full items-center gap-2 px-2 py-1.5 text-left text-xs transition-colors",
                                                                isUsedByOther
                                                                    ? "cursor-not-allowed text-faint opacity-40"
                                                                    : "text-muted-foreground hover:bg-secondary hover:text-foreground"
                                                            )}
                                                        >
                                                            <span className="flex-1 truncate">{project.name}</span>
                                                            {isUsedByOther && <Lock className="h-3 w-3" />}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}
                                </>
                            )}

                            {/* Connecté avec un projet */}
                            {status === 'connected' && (
                                <>
                                    {/* Projet courant — row d'identité */}
                                    <div className="flex items-center gap-2 border border-border bg-secondary p-2">
                                        <Database className="h-4 w-4 text-muted-foreground" />
                                        <span className="flex-1 truncate text-xs font-medium text-foreground">
                                            {selectedProjectName}
                                        </span>
                                        <a
                                            href={`https://supabase.com/dashboard/project/${selectedProject}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="p-1 transition-colors hover:bg-surface-3"
                                            title="Dashboard Supabase"
                                        >
                                            <ExternalLink className="h-3 w-3 text-faint" />
                                        </a>
                                    </div>

                                    {/* Ouvrir le panneau — secondaire */}
                                    {onOpenPanel && (
                                        <button
                                            onClick={() => {
                                                onOpenPanel();
                                                setIsOpen(false);
                                            }}
                                            className={BTN_SECONDARY}
                                        >
                                            <Database className="h-3 w-3" />
                                            Panneau Supabase
                                        </button>
                                    )}

                                    {/* Changer de projet */}
                                    <div className="border-t border-border pt-2">
                                        <p className="mb-2 font-mono text-[10px] uppercase tracking-wider text-faint">
                                            Changer de projet
                                        </p>

                                        {/* Charger les projets — secondaire */}
                                        <button
                                            onClick={handleLoadProjects}
                                            disabled={isLoadingProjects}
                                            className={BTN_SECONDARY}
                                        >
                                            {isLoadingProjects ? (
                                                <>
                                                    <PxMark state="generating" size={10} />
                                                    Chargement…
                                                </>
                                            ) : (
                                                <>
                                                    <Database className="h-3 w-3" />
                                                    Charger les projets
                                                </>
                                            )}
                                        </button>

                                        {/* Créer un projet — secondaire (un seul primaire par zone) */}
                                        <div className="mt-2">
                                            <CreateSupabaseProjectDialog
                                                pixelProjectId={pixelProjectId}
                                                trigger={
                                                    <button className={BTN_SECONDARY}>
                                                        <Plus className="h-3 w-3" />
                                                        Créer un projet
                                                    </button>
                                                }
                                            />
                                        </div>

                                        {/* Project list */}
                                        {projects.length > 0 && (
                                            <div className="mt-2 max-h-32 space-y-1 overflow-y-auto">
                                                {projects.map((project) => {
                                                    const isUsedByOther = project.usedByPixelProjectId &&
                                                        project.usedByPixelProjectId !== pixelProjectId;
                                                    const isCurrentProject = project.id === selectedProject;

                                                    return (
                                                        <button
                                                            key={project.id}
                                                            onClick={() => handleSelectProject(project.id, project.name, !!isUsedByOther)}
                                                            disabled={!!isUsedByOther || isCurrentProject}
                                                            className={cn(
                                                                "flex w-full items-center gap-2 px-2 py-1.5 text-left text-xs transition-colors",
                                                                isCurrentProject
                                                                    ? "bg-secondary text-foreground"
                                                                    : isUsedByOther
                                                                        ? "cursor-not-allowed text-faint opacity-40"
                                                                        : "text-muted-foreground hover:bg-secondary hover:text-foreground"
                                                            )}
                                                        >
                                                            <span className="flex-1 truncate">{project.name}</span>
                                                            {isCurrentProject && <Check className="h-3 w-3 text-ok" />}
                                                            {isUsedByOther && <Lock className="h-3 w-3" />}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>

                                    {/* Déconnecter — ghost err */}
                                    <button
                                        onClick={() => {
                                            disconnect();
                                            setIsOpen(false);
                                        }}
                                        className="flex w-full items-center justify-center gap-2 px-3 py-2 text-xs text-err underline-offset-[3px] transition-colors hover:underline"
                                    >
                                        Déconnecter
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                </>,
                document.body
            )}
        </>
    );
}

export default SupabaseStatusPopover;
